import { Paginate } from './../../entities/common/Paginate';
import { TimeMaterialResponse } from './../../entities/tm/TimeMaterialResponse';
import { Endpoint } from '../endpoint';
import {
  ProposalId,
  ProposalResponse,
  CompactProposalTableResponse,
  ResendProposalResponse,
  ProposalStatus,
  ProposalDepositAchRequest,
  ProposalDepositCardRequest,
  ProposalSigneeResponse,
  ProposalsPageQuery,
  ProposalRevisionResponse,
  ProposalPdfResponse,
} from '@business/entities/proposal';
import { R } from '@core/utils/r';
import { AddProposalFormData } from '@modules/proposals/AddProposalModal/AddProposalModal';
import { LineItemFormData } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import { EventMeta } from '@business/entities/calendar';
import { TMBoardId } from '@business/entities/tm/TimeMaterialBoardResponse';
import { AI98AgentResponse } from '@business/entities/s98ai/AI98AgentResponse';

export function fetchRevisions(id: ProposalId) {
  return Endpoint.get<ProposalRevisionResponse[]>(`@gc/proposals/revisions/${id}`);
}

export function fetchProposals(params?: ProposalsPageQuery) {
  return Endpoint.get<CompactProposalTableResponse[]>(`@gc/proposals`, { params });
}

export function fetchProposalsPaginated(params?: ProposalsPageQuery) {
  return Endpoint.get<Paginate<CompactProposalTableResponse>>(`@gc/proposals`, { params: R.objectCompact(params), headers: { paginated: 'true' } });
}

export function fetchProposal(id: ProposalId, isPublic = false) {
  if (isPublic) return Endpoint.get<ProposalResponse>(`@gc/public/proposals/proposal/${id}`);
  return Endpoint.get<ProposalResponse>(`@gc/proposals/proposal/${id}`);
}

export function createProposal(data: AddProposalFormData, meta?: EventMeta, autoSave = false, isEditing = false) {
  return Endpoint.post<ProposalResponse>(`@gc/proposals`, { data: { ...data, meta, autoSave, isEditing } });
}

export function updateProposal(
  id: ProposalId,
  data: Partial<ProposalResponse>,
  isPublic?: boolean,
  selectedProducts?: LineItemFormData[],
  autoSave = false,
  isEditing = false,
) {
  if (isPublic) return Endpoint.patch<ProposalResponse>(`@gc/public/proposals/${id}`, { data: { ...data, selectedProducts } });
  else return Endpoint.patch<ProposalResponse>(`@gc/proposals/${id}`, { data: { ...data, selectedProducts, autoSave, isEditing } });
}

export function duplicateProposal(
  id: ProposalId,
  data: Partial<ProposalResponse>,
  isPublic?: boolean,
  selectedProducts?: LineItemFormData[],
  autoSave = false,
) {
  return Endpoint.post<ProposalResponse>(`@gc/proposals/duplicate/${id}`, { data: { ...data, selectedProducts, autoSave } });
}
export function newDuplicateProposal(id: ProposalId) {
  return Endpoint.post<ProposalResponse>(`@gc/proposals/duplicate-new/${id}`);
}

export function updateStatus(id: ProposalId, data: { status: ProposalStatus }, isPublic?: boolean) {
  if (isPublic) return Endpoint.patch<ProposalResponse>(`@gc/public/proposals/status/${id}`, { data });
  else return Endpoint.patch<ProposalResponse>(`@gc/proposals/status/${id}`, { data });
}

export function updateTitle(id: ProposalId, title: string) {
  return Endpoint.patch<ProposalResponse>(`@gc/proposals/title/${id}`, { data: { title } });
}

export function deleteProposal(id: ProposalId) {
  return Endpoint.delete<{ openDeals: string[] }>(`@gc/proposals/${id}`);
}

export function getItem(id: ProposalId, isPublic?: boolean) {
  if (isPublic) return Endpoint.get<ProposalResponse>(`@gc/public/proposal/item/${id}`);
  else return Endpoint.get<ProposalResponse>(`@gc/proposals/item/${id}`);
}

export function createHistory(id: ProposalId, message: string, senderId?: string) {
  return Endpoint.post<void>(`@gc/public/proposal/proposal-history/${id}`, { data: { message, senderId } });
}

export function updateSender(id: ProposalId, data: any) {
  return Endpoint.patch<ProposalSigneeResponse>(`@gc/public/proposal/proposal-sender/${id}`, { data });
}

export function resendProposal(id: ProposalId, data: any) {
  return Endpoint.post<void>(`@gc/proposals/resend-proposal/${id}`, { data });
}

export function getProposalResendInfo(id: ProposalId) {
  return Endpoint.get<ResendProposalResponse>(`@gc/proposals/proposal-resend-info/${id}`);
}

export function payWithACH(id: ProposalId, data: ProposalDepositAchRequest) {
  return Endpoint.post<ProposalResponse>(`@gc/proposal-payment/ach/${id}`, { data });
}

export function payWithCard(id: ProposalId, data: ProposalDepositCardRequest) {
  return Endpoint.post<ProposalResponse>(`@gc/proposal-payment/card/${id}`, { data });
}

export function getPaybiltDepositToken(id: ProposalId, amount?: number) {
  return Endpoint.get<{ token: string; baseUrl: string; sdkScriptUrl: string; testMode: boolean }>(
    `@gc/proposal-payment/paybilt-token/${id}`,
    amount ? { params: { amount } } : undefined,
  );
}

export function completePaybiltDeposit(
  id: ProposalId,
  data: {
    txnId: string;
    amount: number;
    method: string;
    senderId?: string;
    senderEmail?: string;
    cardId?: string;
    lastFour?: string;
    brand?: string;
    expiryMonth?: string;
    expiryYear?: string;
  },
) {
  return Endpoint.post<ProposalResponse>(`@gc/proposal-payment/paybilt-complete/${id}`, { data });
}
export function createWorkOrder(id: ProposalId, boardId: TMBoardId) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/proposals/create-work-order/${id}`, { data: { boardId } });
}

export function unarchiveProposal(id: ProposalId) {
  return Endpoint.post<void>(`@gc/proposals/unarchive/${id}`);
}

export function hardDeleteProposal(id: ProposalId) {
  return Endpoint.post<{ openDeals: string[] }>(`@gc/proposals/delete/${id}`);
}

export function printProposal(id: ProposalId) {
  return Endpoint.get<ProposalPdfResponse>(`@gc/proposals/print/${id}`);
}

export function getS98AIAgents() {
  return Endpoint.get<AI98AgentResponse[]>('@gc/proposals/ai98-agents');
}
